/**
 * useVoiceAutoSend — encapsulates the Voice→Quick Buck handoff state machine
 * that lives at the top of AIHub.jsx. Extracted to keep AIHub.jsx focused on
 * the chat UX rather than the gnarly race-condition guards.
 *
 * The hook owns:
 *  - pendingAutoSendRef  → the prefill string captured synchronously on mount
 *  - autoSendActiveRef   → stays truthy through the entire auto-send sequence
 *  - messagesRef         → synchronous mirror of the chat messages state so
 *                          loadThreads() can detect "we already have visible
 *                          messages" BEFORE React commits the next render
 *  - setMessagesSync     → wrapper around setMessages that also updates the ref
 *
 * Why all three flags exist:
 *  Earlier iterations 240 / 241 used only `pendingAutoSendRef`. The bug was
 *  that the auto-send watcher cleared it BEFORE sendMessage's optimistic
 *  insert committed. StrictMode's double-invoke of the [user] effect then
 *  re-ran loadThreads, whose guard now read `null` and auto-selected a
 *  thread, which triggered loadMessages → setMessages([]) and wiped the UI.
 *
 *  iter 242 introduced `autoSendActiveRef` (stays truthy until the optimistic
 *  commit lands) and `messagesRef` (synchronous "we already have a card"
 *  check) — three layers of defense, since any one of them can fail under
 *  weird scheduling.
 *
 * Consumer API:
 *  const { pendingAutoSend, isAutoSendActive, releaseAutoSend,
 *          messagesRef, setMessagesSync } = useVoiceAutoSend({ messages, setMessages });
 *
 *  Then:
 *    - Use `pendingAutoSend.current` in loadThreads guard (legacy compatibility)
 *    - Use `isAutoSendActive()` to block any thread-switch side effects
 *    - Call `releaseAutoSend()` after the optimistic commit + at end of loadThreads
 *    - Use `setMessagesSync(prev => ...)` for optimistic message inserts
 *    - Read `messagesRef.current.length` in loadThreads to detect existing messages
 */
import { useEffect, useRef } from 'react';

export function useVoiceAutoSend({ messages, setMessages }) {
  // Captured synchronously during the initial render so any racing effect
  // sees the pending prefill BEFORE React schedules its first commit.
  const pendingAutoSendRef = useRef(
    (() => {
      try {
        const params = new URLSearchParams(window.location.search);
        if (params.get('prefill') === '1' && params.get('autoSend') === '1') {
          return sessionStorage.getItem('ai_studio_prefill_prompt') || null;
        }
      } catch {
        /* SSR / privacy-mode browsers */
      }
      return null;
    })(),
  );

  // Stays truthy through the ENTIRE auto-send lifecycle so any racing
  // loadThreads / loadMessages call sees the guard. Released by the consumer
  // once the optimistic insert is in state.
  const autoSendActiveRef = useRef(!!pendingAutoSendRef.current);

  // Synchronous mirror of `messages` so we can read it from refs without
  // waiting for React's commit phase. Updated by setMessagesSync immediately
  // AND by an effect (as a safety net for any setMessages call that bypasses
  // the helper).
  const messagesRef = useRef(messages || []);
  useEffect(() => {
    messagesRef.current = messages || [];
  }, [messages]);

  // Use this in place of setMessages for optimistic inserts. It updates the
  // React state AND the synchronous ref in lockstep so guards reading
  // messagesRef see the new value BEFORE the next render commits.
  const setMessagesSync = (updater) => {
    setMessages((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      messagesRef.current = next;
      return next;
    });
  };

  const releaseAutoSend = () => {
    autoSendActiveRef.current = false;
  };

  const isAutoSendActive = () => autoSendActiveRef.current;

  return {
    pendingAutoSend: pendingAutoSendRef,
    isAutoSendActive,
    releaseAutoSend,
    messagesRef,
    setMessagesSync,
  };
}
